import api, { PaginatedApiResponse, ApiResponse } from './axiosInstance';
import { EquipmentListParams, CreateEquipmentRequest } from '../types/equipment';

/**
 * Must stay in sync with backend `createBulkEquipmentSchema` (Joi `items.max(...)`).
 * Payloads must list parents before children so each chunk can be inserted after the previous one.
 */
export const BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST = 1000;

export type BulkEquipmentApiResult = {
  totalCreated: number;
  totalFailed: number;
  created: any[];
  failed: any[];
};

export type CreateBulkEquipmentResponse = ApiResponse<BulkEquipmentApiResult> & {
  meta?: { batched: boolean; batchCount: number; totalRequested: number };
};

export const equipmentService = {
  async getEquipment(params?: EquipmentListParams) {
    const response = await api.get<PaginatedApiResponse<any>>('/equipment', { params });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment');
    const raw = (response.data as any).data;
    if (Array.isArray(raw)) {
      return { success: true, message: response.data.message, data: raw, meta: (response.data as any).meta } as any;
    }
    if (raw && Array.isArray(raw.docs)) {
      return {
        success: true,
        message: response.data.message,
        data: raw.docs,
        meta: {
          page: raw.page || 1,
          limit: raw.limit || raw.docs.length,
          totalPages: raw.totalPages || 1,
          totalDocs: raw.totalDocs || raw.docs.length,
          hasPrevPage: !!raw.hasPrevPage,
          hasNextPage: !!raw.hasNextPage,
          prevPage: raw.prevPage ?? null,
          nextPage: raw.nextPage ?? null,
        },
      } as any;
    }
    // Fallback: wrap single object as array
    return { success: true, message: response.data.message, data: raw ? [raw] : [], meta: (response.data as any).meta } as any;
  },

  async getById(equipmentId: string, includeCheckpoints?: boolean) {
    const path = includeCheckpoints ? `/equipment/${equipmentId}?includeCheckpoints=true` : `/equipment/${equipmentId}`;
    const response = await api.get<ApiResponse<any>>(path);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment');
    return response.data.data;
  },

  async getByIdWithCheckpointsPath(equipmentId: string) {
    const response = await api.get<ApiResponse<any>>(`/equipment/${equipmentId}/includeCheckpoints=true`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment');
    return response.data.data;
  },

  async createEquipment(payload: CreateEquipmentRequest) {
    const response = await api.post<ApiResponse<any>>('/equipment', payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create equipment');
    return response.data.data;
  },

  async updateEquipment(equipmentId: string, payload: Partial<CreateEquipmentRequest>) {
    const response = await api.patch<ApiResponse<any>>(`/equipment/${equipmentId}`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update equipment');
    return response.data.data;
  },

  async deleteEquipment(equipmentId: string) {
    const response = await api.delete<ApiResponse<null>>(`/equipment/${equipmentId}`);
    // Backend returns 204 No Content on successful deletion (no response body)
    // A successful response will have status 204 and no data
    if (response.status === 204 || response.status === 200) {
      return true;
    }
    // If there's a response body with success field, check it
    if (response.data && !response.data.success) {
      throw new Error(response.data.message || 'Failed to delete equipment');
    }
    return true;
  },

  // Aliases for backward compatibility
  async create(payload: CreateEquipmentRequest) {
    return this.createEquipment(payload);
  },

  async update(equipmentId: string, payload: Partial<CreateEquipmentRequest>) {
    return this.updateEquipment(equipmentId, payload);
  },

  async remove(equipmentId: string) {
    return this.deleteEquipment(equipmentId);
  },

  async setCheckpointFrequency(equipmentId: string, checkpoint: string, frequency: string) {
    const response = await api.post<ApiResponse<any>>(`/equipment/${equipmentId}/frequency`, { checkpoint, frequency });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to set frequency');
    return response.data.data;
  },

  async getByProject(
    projectId: string,
    opts?: { view?: 'tree'; includePvModuleSlots?: boolean }
  ) {
    const params: Record<string, string> = {};
    if (opts?.view === 'tree') params.view = 'tree';
    if (opts?.includePvModuleSlots) params.includePvModuleSlots = 'true';
    const response = await api.get<ApiResponse<any[]>>(`/equipment/project/${projectId}`, {
      params: Object.keys(params).length ? params : undefined,
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment by project');
    return response.data.data;
  },

  /** Lazy tree: roots (omit parentId) or children of one parent; rows include `childCount`. */
  async getProjectTreeNodes(
    projectId: string,
    opts?: { parentId?: string; includePvModuleSlots?: boolean; fullSolarDcLayout?: boolean }
  ) {
    const params: Record<string, string> = {};
    if (opts?.parentId) params.parentId = opts.parentId;
    if (opts?.includePvModuleSlots) params.includePvModuleSlots = 'true';
    if (opts?.fullSolarDcLayout) params.fullSolarDcLayout = 'true';
    const response = await api.get<ApiResponse<any[]>>(`/equipment/project/${projectId}/tree-nodes`, {
      params: Object.keys(params).length ? params : undefined,
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment tree nodes');
    return response.data.data;
  },

  /** Persisted equipment row count for the project (single number; no list payload). */
  async getProjectEquipmentCount(projectId: string): Promise<number> {
    const response = await api.get<ApiResponse<{ total: number }>>(`/equipment/project/${projectId}/count`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment count');
    const t = (response.data.data as { total?: number })?.total;
    return typeof t === 'number' && Number.isFinite(t) ? t : 0;
  },

  /** Total equipment + asset breakdown + aggregated PV module count. */
  async getProjectEquipmentSummary(projectId: string): Promise<{
    total: number;
    breakdown: Array<{ label: string; count: number }>;
    moduleCount: number;
  }> {
    const response = await api.get<
      ApiResponse<{
        total: number;
        breakdown: Array<{ label: string; count: number }>;
        moduleCount: number;
      }>
    >(`/equipment/project/${projectId}/equipment-summary`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment summary');
    const d = response.data.data as {
      total?: number;
      breakdown?: Array<{ label: string; count: number }>;
      moduleCount?: number;
    };
    const total = typeof d?.total === 'number' && Number.isFinite(d.total) ? d.total : 0;
    const breakdown = Array.isArray(d?.breakdown) ? d.breakdown : [];
    const moduleCount =
      typeof d?.moduleCount === 'number' && Number.isFinite(d.moduleCount) ? Math.max(0, Math.floor(d.moduleCount)) : 0;
    return { total, breakdown, moduleCount };
  },

  async getByStatus(status: string) {
    const response = await api.get<ApiResponse<any[]>>(`/equipment/status/${status}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment by status');
    return response.data.data;
  },

  async getAssetCheckpoints(assetId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/equipment/asset/${assetId}/checkpoints`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch asset checkpoints');
    return response.data.data;
  },

  async getWithProjectType(equipmentId: string) {
    const response = await api.get<ApiResponse<any>>(`/equipment/${equipmentId}/with-project-type`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment with project type');
    return response.data.data;
  },

  async updateEquipmentStatus(equipmentId: string, status: 'active' | 'inactive') {
    const response = await api.put<ApiResponse<any>>(`/equipment/${equipmentId}/status`, { status });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update equipment status');
    return response.data.data;
  },

  /**
   * Creates equipment via POST /equipment/bulk. Automatically splits into sequential chunks
   * when `items.length` exceeds {@link BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST}.
   * @param responseDetail `minimal` (default) returns slim `created` rows; use `full` if you need complete documents.
   */
  async createBulkEquipment(payload: {
    projectId: string;
    items: any[];
    responseDetail?: 'full' | 'minimal';
  }): Promise<CreateBulkEquipmentResponse> {
    const { projectId, items, responseDetail = 'minimal' } = payload;
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('Items array is required and must not be empty');
    }

    const bulkParams =
      responseDetail === 'full' ? undefined : { responseDetail: 'minimal' as const };

    if (items.length <= BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST) {
      const response = await api.post<ApiResponse<BulkEquipmentApiResult>>(
        '/equipment/bulk',
        { projectId, items },
        { params: bulkParams }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create bulk equipment');
      }
      return response.data as CreateBulkEquipmentResponse;
    }

    const batchCount = Math.ceil(items.length / BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST);
    let totalCreated = 0;
    let totalFailed = 0;
    const created: any[] = [];
    const failed: any[] = [];

    for (let offset = 0; offset < items.length; offset += BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST) {
      const chunk = items.slice(offset, offset + BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST);
      const response = await api.post<ApiResponse<BulkEquipmentApiResult>>(
        '/equipment/bulk',
        { projectId, items: chunk },
        { params: bulkParams }
      );
      if (!response.data.success) {
        throw new Error(
          response.data.message ||
            `Bulk create failed at batch ${offset / BULK_EQUIPMENT_MAX_ITEMS_PER_REQUEST + 1} of ${batchCount}`
        );
      }
      const d = response.data.data;
      if (d) {
        totalCreated += d.totalCreated ?? 0;
        totalFailed += d.totalFailed ?? 0;
        if (Array.isArray(d.created)) created.push(...d.created);
        if (Array.isArray(d.failed)) failed.push(...d.failed);
      }
    }

    return {
      success: true,
      message: `Successfully created ${totalCreated} equipment items`,
      data: { totalCreated, totalFailed, created, failed },
      meta: { batched: true, batchCount, totalRequested: items.length }
    };
  },

  async getEquipmentByProjectAsset(projectAssetId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/equipment/project-asset/${projectAssetId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch equipment by project asset');
    return response.data.data;
  },

  async findNonOperationalEquipmentAtLocation(params: {
    projectId: string;
    assetId: string;
    zone?: string;
    block?: string;
    subLocation?: string;
  }) {
    const queryParams = new URLSearchParams();
    queryParams.append('projectId', params.projectId);
    queryParams.append('assetId', params.assetId);
    if (params.zone) queryParams.append('zone', params.zone);
    if (params.block) queryParams.append('block', params.block);
    if (params.subLocation) queryParams.append('subLocation', params.subLocation);

    const response = await api.get<ApiResponse<any[]>>(`/equipment/location/non-operational?${queryParams.toString()}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch Non-Operational equipment');
    return response.data.data;
  },

  /**
   * Bulk update equipment details (make & model, manufacturer, technical specifications)
   */
  async bulkUpdateEquipment(equipmentIds: string[], updateData: any) {
    const response = await api.put<ApiResponse<any>>('/equipment/bulk-update', {
      equipmentIds,
      updateData
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to bulk update equipment');
    return response.data.data;
  },

  /**
   * Upload documents to a single equipment
   */
  async uploadEquipmentDocuments(equipmentId: string, documents: Array<{
    documentName: string;
    documentType: string;
    documentFile: string;
  }>) {
    const response = await api.post<ApiResponse<any>>(`/equipment/${equipmentId}/documents`, {
      documents
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to upload documents');
    return response.data.data;
  },

  /**
   * Bulk upload documents to multiple equipment
   */
  async bulkUploadDocuments(equipmentIds: string[], documents: Array<{
    documentName: string;
    documentType: string;
    documentFile: string;
  }>) {
    const response = await api.post<ApiResponse<any>>('/equipment/bulk-documents', {
      equipmentIds,
      documents
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to bulk upload documents');
    return response.data.data;
  },

  /**
   * Delete a document from equipment
   */
  async deleteEquipmentDocument(equipmentId: string, documentId: string) {
    const response = await api.delete<ApiResponse<any>>(`/equipment/${equipmentId}/documents/${documentId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to delete document');
    return response.data.data;
  }
};

export default equipmentService;
